import { c as _c } from "react/compiler-runtime";
import type { GetBlockErrorType } from 'viem';
import { capitalize } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import type { InterfaceOperation } from '@/operations/operations.types';
interface FinalizeLabelProps {
  operationLabel: string;
  isInitialState: boolean;
  isNew: boolean;
  isEditing: boolean;
  localErrorMessage?: ReactNode;
  operations: InterfaceOperation[];
  simulationError?: Error;
  blockError: GetBlockErrorType | null;
  isPending: boolean;
  isWrongChain: boolean;
  hasDisabledMessage?: boolean;
  noReviewPrefix?: boolean;
}

/**
 * A custom hook used to generate the label for the finalize button based on the current state of the operation.
 *
 * @param {string} operationLabel - The label for the operation.
 * @param {boolean} isInitialState - A boolean indicating whether the operation is in the initial state.
 * @param {boolean} isNew - A flag indicating whether the operation is new.
 * @param {boolean} isEditing - A flag indicating whether the operation is being edited.
 * @param {string | undefined} localErrorMessage - An optional error message from the local simulation.
 * @param {InterfaceOperation[]} operations - The list of operations.
 * @param {Error | undefined} simulationError - An optional error from the simulation.
 * @param {boolean} hasDisabledMessage - A flag indicating whether the button is disabled.
 * @param {boolean} noReviewPrefix - A flag indicating whether to remove the "Review" prefix from the label.
 * @returns {string} The generated label for the finalize button.
 */
const useFinalizeLabel = t0 => {
  const $ = _c(5);
  const {
    operationLabel,
    isInitialState,
    isNew,
    isEditing,
    localErrorMessage,
    operations,
    simulationError,
    blockError,
    isPending,
    isWrongChain,
    hasDisabledMessage,
    noReviewPrefix
  } = t0;
  let t1;
  bb0: {
    if (hasDisabledMessage) {
      let t2;
      if ($[0] !== operationLabel) {
        t2 = capitalize(operationLabel);
        $[0] = operationLabel;
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      t1 = `${t2} disabled`;
      break bb0;
    }
    if (isInitialState) {
      t1 = "Enter an amount";
      break bb0;
    }
    if (isWrongChain) {
      t1 = "Switch chain";
      break bb0;
    }
    if (!isNew && isEditing) {
      t1 = "Editing";
      break bb0;
    }
    if (blockError && isPending) {
      t1 = "Could not fetch block";
      break bb0;
    }
    if (localErrorMessage) {
      t1 = localErrorMessage;
      break bb0;
    }
    if (simulationError) {
      t1 = simulationError.message;
      break bb0;
    }
    if (operations.length === 0 || operations.length === 1 && !isNew) {
      let t2;
      if ($[2] !== noReviewPrefix || $[3] !== operationLabel) {
        t2 = noReviewPrefix ? capitalize(operationLabel) : `Review ${operationLabel}`;
        $[2] = noReviewPrefix;
        $[3] = operationLabel;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = t2;
      break bb0;
    }
    t1 = "Finalize bundle";
  }
  return t1;
};
export default useFinalizeLabel;